import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X, Trash2, MessageCircle, MapPin, Map } from "lucide-react";
import { useWishlist } from "@/lib/wishlist";
import { waLink } from "@/lib/api";

export function HeartButton({ item, className = "", size = 16 }) {
  const { has, toggle } = useWishlist();
  const on = has(item.id);
  return (
    <button type="button" aria-label={on ? "Remove from wishlist" : "Add to wishlist"} data-testid={`wishlist-heart-${item.slug}`} data-active={on}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(item); }}
      className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-all hover:scale-110 ${on ? "bg-terra text-sand" : "bg-sand/90 text-ink hover:text-terra"} ${className}`}>
      <Heart size={size} className={on ? "fill-current" : ""} />
    </button>
  );
}

export default function WishlistDock() {
  const { list, remove, clear } = useWishlist();
  const [open, setOpen] = useState(false);
  const places = list.filter((x) => x.type === "place");
  const tours = list.filter((x) => x.type === "tour");
  const msg = `Hi Random Rider! Here's my trip wishlist:${tours.length ? `\n\nTours:\n${tours.map((t) => `• ${t.name}${t.meta ? ` (${t.meta})` : ""}`).join("\n")}` : ""}${places.length ? `\n\nPlaces to visit:\n${places.map((p) => `• ${p.name}${p.meta ? ` (${p.meta})` : ""}`).join("\n")}` : ""}\n\nCan you suggest a plan and price?`;

  return (
    <>
      <AnimatePresence>
        {list.length > 0 && (
          <motion.button key="dock" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            onClick={() => setOpen(true)} data-testid="wishlist-open-button"
            className="fixed left-4 bottom-20 sm:left-6 sm:bottom-24 z-40 flex items-center gap-2 bg-ink text-sand pl-3 pr-4 py-2.5 rounded-full shadow-xl shadow-ink/30 hover:bg-terra transition-colors">
            <Heart size={16} className="fill-terra text-terra" />
            <span className="text-sm font-semibold">Wishlist</span>
            <span className="bg-sand text-ink text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="wishlist-count">{list.length}</span>
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
          <>
            <motion.div key="bg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 bg-ink/50 z-[70]" />
            <motion.aside key="panel" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="fixed left-0 top-0 bottom-0 w-full max-w-md bg-sand z-[80] flex flex-col shadow-2xl" data-testid="wishlist-drawer">
              <div className="flex items-center justify-between p-5 border-b border-ink/10">
                <h3 className="font-serif text-2xl font-bold text-ink flex items-center gap-2"><Heart size={20} className="fill-terra text-terra" /> Your wishlist</h3>
                <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white" data-testid="wishlist-close-button"><X size={18} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {[["Tours", tours, Map], ["Places", places, MapPin]].map(([label, arr, Icon]) => arr.length > 0 && (
                  <div key={label}>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-ink/50 mb-2 flex items-center gap-1.5"><Icon size={11} /> {label} · {arr.length}</p>
                    <div className="space-y-2">
                      {arr.map((it) => (
                        <div key={it.id} className="flex items-center gap-3 bg-white border border-ink/10 rounded-xl p-2.5" data-testid={`wishlist-item-${it.slug}`}>
                          <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                          <div className="flex-1 min-w-0">
                            <Link to={it.type === "tour" ? `/tours/${it.slug}` : `/places/${it.slug}`} onClick={() => setOpen(false)} className="text-sm font-semibold text-ink hover:text-terra leading-snug block truncate">{it.name}</Link>
                            {it.meta && <p className="text-[11px] text-ink/50 truncate">{it.meta}</p>}
                          </div>
                          <button onClick={() => remove(it.id)} className="p-2 text-ink/40 hover:text-red-600" data-testid={`wishlist-remove-${it.slug}`}><Trash2 size={15} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {list.length === 0 && <p className="text-sm text-ink/50 text-center py-10" data-testid="wishlist-empty">Tap the heart on any place or tour to save it here.</p>}
              </div>
              <div className="p-5 border-t border-ink/10 space-y-2">
                <a href={waLink(msg)} target="_blank" rel="noreferrer" data-testid="wishlist-whatsapp-button"
                  className={`flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all ${list.length === 0 ? "pointer-events-none opacity-50" : ""}`}>
                  <MessageCircle size={16} /> Send wishlist on WhatsApp
                </a>
                {list.length > 0 && <button onClick={clear} className="w-full text-xs text-ink/50 hover:text-red-600 py-1" data-testid="wishlist-clear-button">Clear wishlist</button>}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
